<template>
  <v-row no-gutters>
    <v-col>
      <!-- *** name-->
      <v-row no-gutters>
        <v-col>
          <h3 style="font-weight: bold; margin-bottom: 18px">
            {{ title ?? 'Registrant Information' }}
          </h3>
        </v-col>
        <v-col cols="12" sm="auto" v-if="copySourceData || copySourceRegistration">
          <v-btn
            class="text-transform-none mb-3"
            variant="outlined"
            size="small"
            prepend-icon="mdi-content-copy"
            :color="eventData?.event_color || 'info'"
            @click="copyFromSource"
          >
            {{ copyButtonLabel }}
          </v-btn>
        </v-col>
      </v-row>
      <v-divider class="mb-3"></v-divider>

      <!-- Ordered form fields (respects field_display_order from ticket/table config) -->
      <template v-for="item in orderedFormItems" :key="`${item.type}-${item.key}`">
        <!-- static: name -->
        <v-row class="mb-4 ga-4" no-gutters v-if="item.type === 'static' && item.key === 'name'">
          <v-col cols="12" lg="6">
            <label for="first-name" class="d-block mb-3"
              >First Name <span v-if="item.config?.required" style="color: red">*</span></label
            >
            <v-text-field
              v-model="first_name"
              id="first-name"
              density="compact"
              variant="outlined"
              hide-details
              :rules="item.config?.required ? [(val) => (val ? true : 'Required')] : []"
              placeholder="First Name"
            />
          </v-col>
          <v-col>
            <label for="last-name" class="d-block mb-3"
              >Last Name <span v-if="item.config?.required" style="color: red">*</span></label
            >
            <v-text-field
              id="last-name"
              v-model="last_name"
              density="compact"
              variant="outlined"
              hide-details
              :rules="item.config?.required ? [(val) => (val ? true : 'Required')] : []"
              placeholder="Last Name"
            />
          </v-col>
        </v-row>

        <!-- static: email -->
        <v-row no-gutters v-else-if="item.type === 'static' && item.key === 'email'">
          <v-col>
            <label for="email" class="d-block mb-3"
              >Email: <span v-if="item.config?.required" style="color: red">*</span></label
            >
            <v-text-field
              id="email"
              placeholder="example@fundeasy.com"
              prepend-inner-icon="mdi-email"
              variant="outlined"
              density="compact"
              v-model="email"
              @blur="onEmailBlur"
              @input="onEmailInput"
              :rules="
                item.config?.required
                  ? [(val) => (isValidEmail(val) ? true : 'Valid email is required')]
                  : []
              "
              :hint="emailIncompleteHint ? 'Looks like this email is incomplete or invalid' : ''"
              :persistent-hint="emailIncompleteHint"
            />
          </v-col>
        </v-row>

        <!-- static: phone_number -->
        <v-row no-gutters v-else-if="item.type === 'static' && item.key === 'phone_number'">
          <v-col>
            <label for="phone_number" class="mb-3 d-inline-block"
              >Phone Number: <span v-if="item.config?.required" style="color: red">*</span></label
            >
            <v-text-field
              prepend-inner-icon="mdi-phone"
              variant="outlined"
              id="phone_number"
              density="compact"
              placeholder="(777) 777 - 7777"
              :model-value="phone_number ? mask.masked(String(phone_number)) : ''"
              @update:modelValue="handlePrimaryContactPhone"
              :rules="[
                (val) => {
                  const digits = mask.unmasked(String(val).replace(/[^0-9]/g, ''));
                  if (!item.config?.required) return true;
                  if (!digits) return 'Required';
                  if (digits.length !== 10) return 'Phone number must be 10 digits';
                  return true;
                },
              ]"
            />
          </v-col>
        </v-row>

        <!-- static: address -->
        <v-row no-gutters v-else-if="item.type === 'static' && item.key === 'address'" class="mb-4">
          <v-col cols="12">
            <label class="d-block mb-3"
              >Address <span v-if="item.config?.required" style="color: red">*</span></label
            >
            <v-text-field
              v-model="address1"
              id="address1"
              density="compact"
              variant="outlined"
              placeholder="Enter Address"
              :rules="item.config?.required ? [(val) => (val ? true : 'Required')] : []"
            />
          </v-col>
          <v-col cols="12">
            <label class="d-block mb-3">Apt., Suite (Optional)</label>
            <v-text-field
              v-model="address2"
              id="address2"
              density="compact"
              variant="outlined"
              placeholder="Apt., Suite (Optional)"
            />
          </v-col>
          <v-col cols="12">
            <label class="d-block mb-3"
              >City <span v-if="item.config?.required" style="color: red">*</span></label
            >
            <v-text-field
              v-model="city"
              id="city"
              density="compact"
              variant="outlined"
              placeholder="City"
              :rules="item.config?.required ? [(val) => (val ? true : 'Required')] : []"
            />
          </v-col>
          <v-col cols="12">
            <v-row no-gutters class="d-flex ga-3">
              <v-col>
                <label class="d-block mb-3"
                  >State <span v-if="item.config?.required" style="color: red">*</span></label
                >
                <v-autocomplete
                  v-model="state"
                  id="state"
                  density="compact"
                  variant="outlined"
                  :items="country?.states ?? []"
                  item-title="name"
                  item-value="acronym"
                  placeholder="State"
                  :rules="item.config?.required ? [(val) => (val ? true : 'Required')] : []"
                />
              </v-col>
              <v-col>
                <label class="d-block mb-3"
                  >Zip <span v-if="item.config?.required" style="color: red">*</span></label
                >
                <v-text-field
                  v-model="postal_code"
                  id="postal_code"
                  density="compact"
                  variant="outlined"
                  placeholder="Zip"
                  :rules="item.config?.required ? [(val) => (val ? true : 'Required')] : []"
                />
              </v-col>
            </v-row>
          </v-col>
          <v-col cols="12">
            <label class="d-block mb-3"
              >Country <span v-if="item.config?.required" style="color: red">*</span></label
            >
            <v-autocomplete
              v-model="country"
              id="country"
              density="compact"
              variant="outlined"
              :items="countries"
              item-title="name"
              item-value="acronym"
              placeholder="Country"
              return-object
              :rules="item.config?.required ? [(val) => (val ? true : 'Required')] : []"
            />
          </v-col>
        </v-row>

        <!-- static: affiliation -->
        <v-row no-gutters v-else-if="item.type === 'static' && item.key === 'affiliation'">
          <v-col>
            <label for="affiliation_dropdown" class="mb-3 d-inline-block"
              >{{ getStaticFieldDisplayLabel(item.config, `Affiliation`) }}:
              <span v-if="item.config?.required" style="color: red">*</span></label
            >
            <v-select
              id="affiliation_dropdown"
              prepend-inner-icon="mdi-share-variant"
              variant="outlined"
              density="compact"
              :placeholder="`Select ${getStaticFieldDisplayLabel(item.config, 'affiliation')}`"
              :no-data-text="'No affiliations available.'"
              :items="availableAffiliationOptions"
              item-title="name"
              item-value="id"
              v-model="affiliationId"
              :rules="item.config?.required ? [(val) => (val ? true : 'Required')] : []"
            />
          </v-col>
          <v-col>
            <v-row class="h-100" no-gutters align="center">
              <v-btn
                style="text-transform: none"
                id="add-to-affiliations"
                variant="text"
                prepend-icon="mdi-plus"
                :color="eventData?.event_color || 'info'"
                @click="openAddAffiliationModal = true"
                >Add New</v-btn
              >
            </v-row>
          </v-col>
        </v-row>

        <!-- static: registration_request -->
        <v-row
          v-else-if="item.type === 'static' && item.key === 'registration_request'"
          class="mb-5"
          no-gutters
        >
          <v-col>
            <label for="registration_request" class="mb-3 d-inline-block"
              >{{ getStaticFieldDisplayLabel(item.config, 'Registration Request') }}:
              <span v-if="item.config?.required" style="color: red">*</span>
            </label>
            <v-text-field
              prepend-inner-icon="mdi-account-group-outline"
              id="registration_request"
              variant="outlined"
              density="compact"
              :placeholder="getStaticFieldDisplayLabel(item.config, 'Registration Request')"
              persistent-hint
              hint="Special requests for registration."
              v-model="registration_request"
              :rules="item.config?.required ? [(val) => (val ? true : 'Required')] : []"
            />
          </v-col>
        </v-row>

        <!-- static: dietary_restrictions -->
        <v-row no-gutters v-else-if="item.type === 'static' && item.key === 'dietary_restrictions'">
          <v-col>
            <label for="dietary_restrictions" class="mb-3 d-inline-block"
              >Dietary Restrictions
              <span v-if="item.config?.required" style="color: red">*</span></label
            >
            <v-text-field
              v-model="dietary_restrictions"
              id="dietary_restrictions"
              density="compact"
              variant="outlined"
              :rules="item.config?.required ? [(val) => (val ? true : 'Required')] : []"
              placeholder="Dietary Restrictions"
            />
          </v-col>
        </v-row>

        <!-- meal choice -->
        <v-row no-gutters v-else-if="item.type === 'meal'" class="my-2">
          <v-col cols="12" v-if="item.addon?.visible">
            <p class="d-inline-block mb-1">
              {{ item.addon.name }}
              <span v-if="item.addon?.required" style="color: red">*</span>
            </p>
            <p class="mb-2" v-if="item.addon.variant_question">
              {{ item.addon.variant_question }}
              <span style="color: red">*</span>
            </p>
            <v-row v-if="item.addon.variants">
              <v-col>
                <v-radio-group
                  v-model="selectedAddonCustomQuestionsAnswer[item.tempId].variant_answer"
                >
                  <div class="pb-2" v-for="variant of item.addon.variants" :key="variant.id">
                    <v-radio
                      :value="variant"
                      :disabled="isVariantSoldOut(variant)"
                      class="align-start"
                    >
                      <template v-slot:label>
                        <v-row no-gutters>
                          <v-col>
                            <p>
                              {{ variant.name }}
                              <span v-if="isVariantSoldOut(variant)">(Sold Out)</span>
                            </p>
                            <p class="text-caption text-medium-emphasis">
                              {{ variant.description }}
                            </p>
                          </v-col>
                        </v-row>
                      </template>
                    </v-radio>
                  </div>
                </v-radio-group>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <!-- custom question -->
        <v-row no-gutters v-else-if="item.type === 'question'">
          <v-col v-if="item.question.is_public && shouldShowQuestion(item.question, false)">
            <label :for="`ticket-question-${item.answerIndex}`" class="d-block mb-2">
              {{ item.question?.question }}
              <span style="color: red" v-if="item.question.required">*</span>
            </label>

            <p
              v-if="item.question?.helper_text"
              class="fs-14 color-neutral-700 mb-2 overflow-y-auto"
              style="max-height: 12rem"
            >
              {{ item.question.helper_text }}
            </p>
            <v-row no-gutters>
              <v-text-field
                variant="outlined"
                :id="`ticket-question-${item.answerIndex}`"
                v-if="item.question?.answer_format_config?.type === 'text-short'"
                density="compact"
                v-model="ticketCustomQuestionsAnswer[item.answerIndex]"
              ></v-text-field>
              <v-textarea
                variant="outlined"
                :id="`ticket-question-${item.answerIndex}`"
                v-if="item.question?.answer_format_config?.type === 'text-long'"
                rows="4"
                density="compact"
                v-model="ticketCustomQuestionsAnswer[item.answerIndex]"
              ></v-textarea>
              <v-row no-gutters v-if="item.question?.answer_format_config?.type === 'checkbox'">
                <v-checkbox
                  v-for="(option, k) of item.question?.answer_format_config?.options"
                  :key="k"
                  :id="`ticket-checkbox-${item.answerIndex}_${k}`"
                  :label="option"
                  density="compact"
                  :color="eventData?.event_color || 'info'"
                  :value="option"
                  v-model="ticketCustomQuestionsAnswer[item.answerIndex]"
                ></v-checkbox>
              </v-row>
              <v-text-field
                class="mt-2"
                v-if="item.question?.answer_format_config?.type === 'number'"
                :id="`ticket-question-${item.answerIndex}`"
                density="compact"
                :color="eventData?.event_color || 'info'"
                :model-value="ticketCustomQuestionsAnswer[item.answerIndex]"
                type="number"
                variant="outlined"
                :hint="makeNumberHintMsg(item.question?.answer_format_config?.options)"
                :rules="[
                  (val) => {
                    if (!val && val !== 0) return true;
                    const num = Number(val);
                    if (isNaN(num)) return 'Please enter a valid number';
                    if (
                      item.question?.answer_format_config?.options?.setMin &&
                      num < Number(item.question?.answer_format_config?.options?.min)
                    )
                      return `Value must be at least ${item.question?.answer_format_config?.options?.min}`;
                    if (
                      item.question?.answer_format_config?.options?.setMax &&
                      num > Number(item.question?.answer_format_config?.options?.max)
                    )
                      return `Value must be at most ${item.question?.answer_format_config?.options?.max}`;
                    return true;
                  },
                ]"
                @update:modelValue="
                  handleNumberUpdate(
                    $event,
                    item.question?.answer_format_config?.options,
                    item.answerIndex,
                  )
                "
              ></v-text-field>
              <v-col
                cols="12"
                v-if="item.question.answer_format_config?.type === 'datetime'"
                class="mt-2"
              >
                <v-row
                  no-gutters
                  v-if="
                    item.question.answer_format_config?.options?.allowDate ||
                    item.question.answer_format_config?.options?.allowTime
                  "
                >
                  <v-col
                    v-if="item.question.answer_format_config?.options?.allowDate"
                    :class="item.question.answer_format_config?.options?.allowTime ? 'mr-2' : ''"
                  >
                    <v-text-field
                      density="compact"
                      variant="outlined"
                      :id="`ticket-question-${item.answerIndex}-date`"
                      v-model="ticketCustomQuestionsAnswer[item.answerIndex].date"
                      label="Date"
                      type="date"
                      @update:model-value="validateDateTimeRange(item.answerIndex)"
                      :error="!!dateTimeRangeErrors[item.answerIndex]"
                    ></v-text-field>
                  </v-col>
                  <v-col v-if="item.question.answer_format_config?.options?.allowTime">
                    <v-text-field
                      density="compact"
                      variant="outlined"
                      :id="`ticket-question-${item.answerIndex}-time`"
                      v-model="ticketCustomQuestionsAnswer[item.answerIndex].time"
                      label="Time"
                      type="time"
                      @update:model-value="validateDateTimeRange(item.answerIndex)"
                      :error="!!dateTimeRangeErrors[item.answerIndex]"
                    ></v-text-field>
                  </v-col>
                </v-row>
                <v-row
                  no-gutters
                  class="mt-4"
                  v-if="item.question.answer_format_config?.options?.allowDateRange"
                >
                  <v-col class="mr-2">
                    <v-text-field
                      density="compact"
                      variant="outlined"
                      :id="`ticket-question-${item.answerIndex}-start-date`"
                      v-model="ticketCustomQuestionsAnswer[item.answerIndex].start_date"
                      label="Start Date"
                      type="date"
                      @update:model-value="validateDateTimeRange(item.answerIndex)"
                      :error="!!dateTimeRangeErrors[item.answerIndex]"
                    ></v-text-field>
                  </v-col>
                  <v-col>
                    <v-text-field
                      density="compact"
                      variant="outlined"
                      :id="`ticket-question-${item.answerIndex}-end-date`"
                      v-model="ticketCustomQuestionsAnswer[item.answerIndex].end_date"
                      label="End Date"
                      type="date"
                      @update:model-value="validateDateTimeRange(item.answerIndex)"
                      :error="!!dateTimeRangeErrors[item.answerIndex]"
                    ></v-text-field>
                  </v-col>
                </v-row>
                <v-row
                  no-gutters
                  class="mt-4"
                  v-if="item.question.answer_format_config?.options?.allowStartEnd"
                >
                  <v-col class="mr-2">
                    <v-text-field
                      density="compact"
                      variant="outlined"
                      :id="`ticket-question-${item.answerIndex}-start-time`"
                      v-model="ticketCustomQuestionsAnswer[item.answerIndex].start_time"
                      label="Start Time"
                      type="time"
                      @update:model-value="validateDateTimeRange(item.answerIndex)"
                      :error="!!dateTimeRangeErrors[item.answerIndex]"
                    ></v-text-field>
                  </v-col>
                  <v-col>
                    <v-text-field
                      density="compact"
                      variant="outlined"
                      :id="`ticket-question-${item.answerIndex}-end-time`"
                      v-model="ticketCustomQuestionsAnswer[item.answerIndex].end_time"
                      label="End Time"
                      type="time"
                      @update:model-value="validateDateTimeRange(item.answerIndex)"
                      :error="!!dateTimeRangeErrors[item.answerIndex]"
                      :error-messages="dateTimeRangeErrors[item.answerIndex] || ''"
                    ></v-text-field>
                  </v-col>
                </v-row>
              </v-col>
              <v-select
                v-if="item.question.answer_format_config?.type === 'dropdown'"
                variant="outlined"
                :id="`ticket-question-${item.answerIndex}-dropdown`"
                v-model="ticketCustomQuestionsAnswer[item.answerIndex]"
                density="compact"
                :items="item.question.answer_format_config?.options"
              ></v-select>
              <v-radio-group
                v-if="item.question.answer_format_config?.type === 'radio'"
                v-model="ticketCustomQuestionsAnswer[item.answerIndex]"
              >
                <v-radio
                  v-for="(option, m) of item.question?.answer_format_config?.options"
                  :key="m"
                  :label="option"
                  density="compact"
                  :id="`ticket-question-${item.answerIndex}-radio-${m}`"
                  :color="eventData?.event_color || 'info'"
                  :value="option"
                ></v-radio>
              </v-radio-group>
            </v-row>
          </v-col>
        </v-row>
      </template>

      <!-- Guest Invite Checkbox (primary registrant only) -->
      <v-row
        no-gutters
        v-if="
          isPrimary &&
          eventData?.recruiter_mode &&
          ticketConfig?.guest_invite_mode === 'opt_in' &&
          ticketConfig?.allow_guest_invites &&
          !isPrepaidReg
        "
        class="mb-4"
      >
        <v-col cols="12">
          <v-checkbox v-model="inviteOthers" class="align-start" hide-details>
            <template v-slot:label>
              <div>
                <p
                  class="fs-16 font-weight-medium"
                  style="color: var(--fundeasy-colors-neutral-900)"
                >
                  I'd like to invite others to join me
                </p>
                <p
                  class="fs-16 text-medium-emphasis"
                  style="color: var(--fundeasy-colors-neutral-600)"
                >
                  You'll receive a link to manage your group and invite guests after registering.
                </p>
              </div>
            </template>
          </v-checkbox>
        </v-col>
      </v-row>

      <!-- Host Referral Selector (primary registrant only, when enabled & not inviting others) -->
      <v-row no-gutters v-if="showHostReferral && !inviteOthers && !isPrepaidReg" class="mb-4">
        <v-col>
          <label class="d-block mb-3">Select Host Name:</label>
          <v-select
            v-model="selectedHostInviteCode"
            :items="[{ title: 'No Host', value: null }, ...(hostItems ?? [])]"
            item-title="title"
            item-value="value"
            variant="outlined"
            hide-details
            density="compact"
            clearable
            @update:modelValue="
              (val) => {
                if (val === null) selectedHostInviteCode = null;
              }
            "
          />
        </v-col>
      </v-row>

      <slot
        name="submit"
        :allMandatoryDataCollected="allMandatoryDataCollected"
        :handleSave="handleSave"
      >
        <v-row class="mt-13" no-gutters justify="center">
          <v-btn
            style="color: var(--fundeasy-colors-generic-00); height: 48px"
            :disabled="!allMandatoryDataCollected"
            class="text-transform-none"
            id="save-btn"
            :color="eventData?.event_color || 'info'"
            variant="elevated"
            @click="handleSave"
            >Save and Continue</v-btn
          >
        </v-row>
      </slot>
    </v-col>
  </v-row>
  <AffiliationCreationModal
    :modal-open="openAddAffiliationModal"
    :event-color="eventData?.event_color || 'info'"
    @close="openAddAffiliationModal = false"
    @created="handleAffiliationCreated"
  />
</template>

<script setup>
import { isValidEmail } from '@/utils/validators';
import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import lodashUtils from 'lodash/util';
import { Mask } from 'maska';
import countries from '@/api/countries';
import { computed, onBeforeMount, onMounted, ref, watch } from 'vue';
import AffiliationCreationModal from '@/components/modals/AffiliationCreationModal.vue';
import BaseUrl from '@/api/BaseUrl';
import ENDPOINTS from '@/api/endpoints';
import { useSnackbarStore } from '@/stores/snackbarInfo';
import useFeatureFlagsStore from '@/stores/featureFlagStore';
const snackbar = useSnackbarStore();

const { uniqueId } = lodashUtils;
const { flagsData } = storeToRefs(useFeatureFlagsStore());
const mask = new Mask({ mask: '(###) ###-####' });

const props = defineProps([
  'ticketConfig',
  'isPrepaidReg',
  'eventData',
  'savedData',
  'title',
  'isGroupHostCheckoutFlow',
  'allCustomQuestions',
  'allMealChoices',
  'showHostReferral',
  'hostItems',
  'copySourceData',
  'copySourceRegistration',
  'copyButtonLabel',
  'isPrimary',
]);
const emit = defineEmits(['save-registration', 'stash-state']);

const dietary_restrictions = ref('');
const first_name = ref('');
const last_name = ref('');
const email = ref('');
const emailTouched = ref(false);

const emailIncompleteHint = computed(() => {
  return email.value && !isValidEmail(email.value);
});

function onEmailBlur() {
  emailTouched.value = true;
}

function onEmailInput() {
  // reserved for future behaviors; keep for parity with dashboard form
}
const phone_number = ref('');
const affiliationId = ref('');
const address1 = ref('');
const address2 = ref('');
const city = ref('');
const state = ref(null);
const postal_code = ref('');
const country = ref(countries[0]); // Default to United States
const groupHostToken = ref('');
const registration_request = ref('');
const selectedAddOns = ref([]);
const includedAddOns = ref([]);
const includedAddOnsCustomQuestionsAnswer = ref([]);
const addOnQty = ref({});
const ticketCustomQuestionsAnswer = ref([]);
const selectedAddonCustomQuestionsAnswer = ref({});
const includedAddOnVariant = ref([]);
const donation = ref(null);
const meal_choice_radio_control = ref({});
const persistedAffiliation = ref({});
const openAddAffiliationModal = ref(false);
const customDonation = ref(false);
const selectedDonationType = ref(null);
const selectedHostInviteCode = ref(null);
const copyButtonLabel = computed(() => props.copyButtonLabel ?? 'Copy from primary');
const inviteOthers = ref(false);

watch(inviteOthers, (newVal) => {
  if (newVal) {
    selectedHostInviteCode.value = null;
  }
});

watch(
  [() => props.ticketConfig, () => props.isPrimary, () => props.isPrepaidReg],
  ([newConfig, isPrimary, isPrepaidReg]) => {
    if (isPrepaidReg || !isPrimary) {
      inviteOthers.value = false;
    } else if (newConfig?.guest_invite_mode === 'automatic' && newConfig?.allow_guest_invites) {
      inviteOthers.value = true;
    } else {
      inviteOthers.value = false;
    }
  },
  { immediate: true },
);

const sortAffiliations = (affiliations = []) =>
  [...affiliations].sort((a, b) =>
    String(a?.name || '').localeCompare(String(b?.name || ''), undefined, { sensitivity: 'base' }),
  );

const getStaticFieldDisplayLabel = (config, fallback) => {
  const customLabel = config?.custom_label?.trim();
  return customLabel || fallback;
};

const hasVisibleStaticField = (key) => !!effectiveFormConfig.value?.[key]?.visible;

const findCountryByCode = (code) => {
  if (!code) return country.value;
  return countries.find((item) => item.acronym === code) ?? country.value;
};

const copyStaticFieldsFromSource = (source) => {
  if (!source) return;

  if (hasVisibleStaticField('email')) email.value = source.email ?? '';
  if (hasVisibleStaticField('phone_number')) phone_number.value = source.phone_number ?? '';
  if (hasVisibleStaticField('affiliation')) {
    affiliationId.value = source.affiliationId ?? source.affiliation_id ?? '';
    persistedAffiliation.value = cloneDeep(
      source.persistedAffiliation ?? persistedAffiliation.value,
    );
  }
  if (hasVisibleStaticField('address')) {
    address1.value = source.address1 ?? '';
    address2.value = source.address2 ?? '';
    city.value = source.city ?? '';
    state.value = source.state ?? null;
    postal_code.value = source.postal_code ?? '';
    country.value = findCountryByCode(source.country_code);
  }
  if (hasVisibleStaticField('registration_request'))
    registration_request.value = source.registration_request ?? '';
  if (hasVisibleStaticField('dietary_restrictions'))
    dietary_restrictions.value = source.dietary_restrictions ?? '';
};

const copyTicketQuestionAnswersFromRegistration = (sourceRegistration) => {
  const answersByQuestionId =
    sourceRegistration?.custom_questions?.reduce((acc, item) => {
      if (item?.id != null) acc[String(item.id)] = item.value;
      return acc;
    }, {}) ?? {};

  if (Object.keys(answersByQuestionId).length === 0) return;

  const nextAnswers = cloneDeep(ticketCustomQuestionsAnswer.value);
  filteredMergedCustomQuestions.value.forEach((question, index) => {
    const answer = answersByQuestionId[String(question.id)];
    if (answer !== undefined) nextAnswers[index] = cloneDeep(answer);
  });
  ticketCustomQuestionsAnswer.value = nextAnswers;
};

const copyIncludedAddonAnswersFromRegistration = (sourceRegistration) => {
  const sourceAddonsById =
    sourceRegistration?.add_ons?.reduce((acc, addon) => {
      if (addon?.id != null) acc[String(addon.id)] = addon;
      return acc;
    }, {}) ?? {};

  if (Object.keys(sourceAddonsById).length === 0) return;

  const nextAnswers = cloneDeep(includedAddOnsCustomQuestionsAnswer.value);
  const nextVariants = cloneDeep(includedAddOnVariant.value);

  includedAddOns.value.forEach((addon, addonIndex) => {
    const sourceAddon = sourceAddonsById[String(addon.id)];
    if (!sourceAddon) return;

    if (sourceAddon.variant) {
      nextVariants[addonIndex] = cloneDeep(sourceAddon.variant);
    }

    const sourceQuestionsById =
      sourceAddon.custom_questions?.reduce((acc, item) => {
        if (item?.id != null) acc[String(item.id)] = item.value;
        return acc;
      }, {}) ?? {};

    addon.custom_questions?.forEach((question, questionIndex) => {
      const answer = sourceQuestionsById[String(question.id)];
      if (answer !== undefined) nextAnswers[addonIndex][questionIndex] = cloneDeep(answer);
    });
  });

  includedAddOnsCustomQuestionsAnswer.value = nextAnswers;
  includedAddOnVariant.value = nextVariants;
};

const copySelectedAddonAnswersFromSource = (sourceData) => {
  const sourceAnswersByAddonId = Object.values(
    sourceData?.selectedAddonCustomQuestionsAnswer ?? {},
  ).reduce((acc, answer) => {
    if (answer?.addon_id != null) acc[String(answer.addon_id)] = answer;
    return acc;
  }, {});

  if (Object.keys(sourceAnswersByAddonId).length === 0) return;

  const nextAnswers = cloneDeep(selectedAddonCustomQuestionsAnswer.value);
  Object.entries(nextAnswers).forEach(([tempId, answer]) => {
    const sourceAnswer = sourceAnswersByAddonId[String(answer?.addon_id)];
    if (!sourceAnswer) return;

    nextAnswers[tempId] = {
      ...answer,
      variant_answer: cloneDeep(sourceAnswer.variant_answer ?? answer.variant_answer),
      answers: cloneDeep(sourceAnswer.answers ?? answer.answers),
    };
  });
  selectedAddonCustomQuestionsAnswer.value = nextAnswers;
};

const copyFromSource = () => {
  const sourceData = props.copySourceData ?? {};
  const sourceRegistration = props.copySourceRegistration ?? {};

  copyStaticFieldsFromSource({ ...sourceRegistration, ...sourceData });
  copyTicketQuestionAnswersFromRegistration(sourceRegistration);
  copyIncludedAddonAnswersFromRegistration(sourceRegistration);
  copySelectedAddonAnswersFromSource(sourceData);
};

const availableAffiliationOptions = ref([]); // load with ["string"]
const availableAffiliationData = ref([]); // load with ["string"]

const handleAffiliationCreated = (createdAffiliation) => {
  persistedAffiliation.value = createdAffiliation;
  availableAffiliationData.value = sortAffiliations([
    ...availableAffiliationData.value,
    createdAffiliation,
  ]);
  affiliationId.value = createdAffiliation.id;
  openAddAffiliationModal.value = false;
};

watch(
  availableAffiliationData,
  (value) => {
    availableAffiliationOptions.value = sortAffiliations(value);
  },
  { deep: true },
);

const formatPrice = (config) => {
  if (config?.addon_type === 'Meal Choice') {
    if (!config?.variants || config?.variants.length === 0) {
      return Number(config?.alacart_price || 0)?.toFixed(2);
    }
    let min = config?.variants?.reduce((a, curr) =>
      Math.min(Number(typeof a === 'object' ? a?.alacart_price : a), Number(curr?.alacart_price)),
    );
    let max = config?.variants?.reduce((a, curr) =>
      Math.max(Number(typeof a === 'object' ? a?.alacart_price : a), Number(curr?.alacart_price)),
    );
    if (typeof min === 'object') min = min?.alacart_price;
    if (typeof max === 'object') max = max?.alacart_price;
    return min === max
      ? Number(min).toFixed(2)
      : `${Number(min).toFixed(2)} - $${Number(max).toFixed(2)}`;
  }
  return Number(config?.alacart_price)?.toFixed(2);
};

// Helper: check if a variant is sold out based on purchase_limit and totalSold
const isVariantSoldOut = (variant) => {
  if (!variant) return false;
  if (variant.purchase_limit === null) return false;
  const limit = Number(variant.purchase_limit ?? 0);
  if (!limit || limit <= 0) return true; // 0 or undefined => no limit
  const sold = Number(variant.totalSold ?? 0);
  return sold >= limit;
};

const makeDefaultNumber = (configOptions) => {
  if (!configOptions) {
    return 0;
  }

  const min = configOptions.setMin && parseInt(configOptions.min);

  if (typeof min === 'number') {
    return min;
  }

  const max = configOptions.setMax && parseInt(configOptions.max);

  if (typeof max === 'number') {
    return max;
  }

  return 0;
};

const selectedAddonQuantities = computed(() => {
  const quantities = {};
  selectedAddOns.value.forEach((addon) => {
    const addonId = String(addon.id);
    quantities[addonId] = addOnQty.value[addonId]?.length || 0;
  });
  return quantities;
});

const getCurrentAvailableQty = (addon) => {
  const selectedQty = selectedAddonQuantities.value[String(addon.id)] || 0;
  const originalQty = addon.available_qty;

  if (originalQty === null || originalQty === undefined) {
    return null; // No limit
  }

  return Math.max(0, originalQty - selectedQty);
};

watch(
  selectedAddOns,
  (addons) => {
    // Keep track of addon IDs that were previously selected but are now deselected
    const previouslySelectedAddonIds = Object.keys(addOnQty.value);
    const currentlySelectedAddonIds = addons.map((addon) => String(addon.id));

    // These are the addons that were unchecked
    const removedAddonIds = previouslySelectedAddonIds.filter(
      (id) => !currentlySelectedAddonIds.includes(id),
    );

    // Make copies of data to work with
    const addOnQtyCopy = cloneDeep(addOnQty.value);
    const answersCopy = cloneDeep(selectedAddonCustomQuestionsAnswer.value);

    // Remove entries for unchecked addons
    removedAddonIds.forEach((addonId) => {
      if (addOnQtyCopy[addonId]) {
        addOnQtyCopy[addonId].forEach((tempId) => {
          delete answersCopy[tempId];
        });
        delete addOnQtyCopy[addonId];
      }
    });

    // Initialize qty arrays for newly selected addons
    addons.forEach((addon) => {
      const addonId = String(addon.id);
      if (!addOnQtyCopy[addonId] || addOnQtyCopy[addonId].length === 0) {
        // Check if there's available quantity before allowing selection
        const currentAvailable = getCurrentAvailableQty(addon);
        if (currentAvailable === null || currentAvailable > 0) {
          // For new addon selections, initialize with one item
          addOnQtyCopy[addonId] = [uniqueId('_temp')];
        } else {
          // If no quantity available, don't allow selection
          const index = selectedAddOns.value.findIndex((a) => a.id === addon.id);
          if (index > -1) {
            selectedAddOns.value.splice(index, 1);
          }
          return;
        }
      }
    });

    // Update refs with our modified copies
    addOnQty.value = addOnQtyCopy;
    selectedAddonCustomQuestionsAnswer.value = answersCopy;

    // Initialize selectedAddonCustomQuestionsAnswer for each addon quantity
    addons.forEach((addon) => {
      const addonId = String(addon.id);
      addOnQty.value[addonId]?.forEach((tempId) => {
        if (!selectedAddonCustomQuestionsAnswer.value[tempId]) {
          selectedAddonCustomQuestionsAnswer.value[tempId] = {
            addon_id: addon.id,
            name: addon.name,
            addon_type: addon.addon_type,
            is_dietary_restriction: addon?.is_dietary_restriction,
            customQuestions: addon.custom_questions,
            hasVariant: addon.variants?.length,
            variant_question: addon.variant_question,
            variants: addon.variants,
            variant_answer: '',
            answers: addon.custom_questions.map((q) => {
              if (q?.answer_format_config?.type === 'checkbox') {
                return [];
              }
              if (q?.answer_format_config?.type?.includes('text')) {
                return '';
              }
              if (q?.answer_format_config?.type === 'number') {
                return makeDefaultNumber(q?.answer_format_config?.options);
              }
              if (q?.answer_format_config?.type === 'datetime') {
                return {};
              }
              return '';
            }),
          };
        }
      });
    });
  },
  {
    deep: true,
  },
);

const addQty = (tempId, addon) => {
  const currentAvailable = getCurrentAvailableQty(addon);

  // Check if we've reached the available quantity limit
  if (currentAvailable !== null && currentAvailable <= 0) {
    // Don't add more if we've reached the limit
    return;
  }

  addOnQty.value[addon.id].push(tempId);
  selectedAddonCustomQuestionsAnswer.value[tempId] = {
    addon_id: addon.id,
    name: addon.name,
    addon_type: addon.addon_type,
    is_dietary_restriction: addon?.is_dietary_restriction,
    customQuestions: addon.custom_questions,
    hasVariant: addon.variants?.length,
    variant_question: addon.variant_question,
    variants: addon.variants,
    variant_answer: '',
    answers: addon.custom_questions.map((q) => {
      if (q?.answer_format_config?.type === 'checkbox') {
        return [];
      }
      if (q?.answer_format_config?.type?.includes('text')) {
        return '';
      }
      if (q?.answer_format_config?.type === 'number') {
        return makeDefaultNumber(q?.answer_format_config?.options);
      }
      if (q?.answer_format_config?.type === 'datetime') {
        return {};
      }
      return '';
    }),
  };
};

const removeQty = (tempId, addon) => {
  addOnQty[addon.id] = addOnQty.value[addon.id].filter((id) => id !== tempId);
  const c = cloneDeep(selectedAddonCustomQuestionsAnswer.value);
  delete c[tempId];
  selectedAddonCustomQuestionsAnswer.value = c;
};
const additionalAddons = computed(() => {
  return (
    props.ticketConfig?.all_available_addons?.reduce((accum, curr) => {
      if (!props.ticketConfig?.included_addons?.find((el) => el.id === curr.id)) {
        accum.push(curr);
      }
      return accum;
    }, []) ?? []
  );
});
const hasAdditionalInfo = computed(() => {
  if ((mergedCustomQuestions.value ?? []).length > 0) {
    return true;
  }
  return !!includedAddOns.value?.some((addon) => addon.custom_questions?.length > 0);
});

// Helper method to check if an addon is at its quantity limit
const isAddonAtQtyLimit = (addon) => {
  const currentAvailable = getCurrentAvailableQty(addon);
  return currentAvailable !== null && currentAvailable <= 0;
};

const isAddonDisabled = (addon) => {
  const currentAvailable = getCurrentAvailableQty(addon);
  const isSelected = selectedAddOns.value.find((a) => a.id === addon.id);

  // If already selected, don't disable the checkbox
  if (isSelected) return false;

  // If no quantity tracking or quantity available, not disabled
  return currentAvailable !== null && currentAvailable <= 0;
};
// Registration form configuration (ticket and table) and merged helpers
const registrationFormConfiguration = computed(
  () => props.ticketConfig?.registration_form_configuration ?? null,
);
const registrationFormConfig = computed(() => registrationFormConfiguration.value?.config ?? {});
const registrationFormCustomQuestions = computed(
  () => registrationFormConfiguration.value?.custom_questions ?? [],
);
const registrationFormMealChoices = computed(
  () => registrationFormConfiguration.value?.meal_choices ?? [],
);

const registrationFormTableConfiguration = computed(
  () => props.ticketConfig?.table_form_configuration ?? null,
);
const registrationFormTableConfig = computed(
  () => registrationFormTableConfiguration.value?.config ?? {},
);
const registrationFormTableCustomQuestions = computed(
  () => registrationFormTableConfiguration.value?.custom_questions ?? [],
);

// Determine which custom questions should be displayed and merge with allCustomQuestions
const mergedCustomQuestions = computed(() => {
  const allQuestions = props.allCustomQuestions ?? [];
  const isTableCase = props.ticketConfig?.table_id != null;

  const normalizeToMap = (raw) => {
    if (!raw) return {};
    if (Array.isArray(raw)) {
      return raw.reduce((acc, item) => {
        if (item && item.id != null) acc[String(item.id)] = item;
        return acc;
      }, {});
    }
    return raw;
  };

  const ticketQuestionsMap = normalizeToMap(registrationFormCustomQuestions.value ?? {});
  const tableQuestionsMap = normalizeToMap(registrationFormTableCustomQuestions.value ?? {});

  const visibleQuestionIds = new Set();

  Object.entries(ticketQuestionsMap).forEach(([idStr, qObj]) => {
    const idNum = Number(idStr);
    if (qObj && idStr != null && qObj.visible !== false && !Number.isNaN(idNum)) {
      visibleQuestionIds.add(idNum);
    }
  });

  if (isTableCase) {
    Object.entries(tableQuestionsMap).forEach(([idStr, qObj]) => {
      const idNum = Number(idStr);
      if (
        qObj &&
        idStr != null &&
        qObj.visible !== false &&
        !Number.isNaN(idNum) &&
        !visibleQuestionIds.has(idNum)
      ) {
        visibleQuestionIds.add(idNum);
      }
    });
  }

  return allQuestions
    .filter((q) => q && q.id != null && visibleQuestionIds.has(Number(q.id)))
    .map((q) => {
      const idKey = String(q.id);
      const tableFormQ = tableQuestionsMap[idKey] ?? null;
      const ticketFormQ = ticketQuestionsMap[idKey] ?? null;

      return {
        ...q,
        ...(ticketFormQ || {}),
        ...(tableFormQ || {}),
      };
    });
});

// merged meal choices between global meal choices and registration form config
const mergedMealChoices = computed(() => {
  const allMeals = props.allMealChoices ?? [];
  const mealChoicesMap = registrationFormMealChoices.value ?? {};

  const normalizeToMap = (raw) => {
    if (!raw) return {};
    if (Array.isArray(raw)) {
      return raw.reduce((acc, item) => {
        if (item && item.id != null) acc[String(item.id)] = item;
        return acc;
      }, {});
    }
    return raw;
  };

  const mealChoicesCfgMap = normalizeToMap(mealChoicesMap);

  return allMeals
    .filter((meal) => meal && meal.addon_type === 'Meal Choice')
    .map((meal) => {
      const idKey = String(meal.id);
      const mealCfg = mealChoicesCfgMap[idKey] ?? null;
      return {
        ...meal,
        ...(mealCfg || {}),
      };
    });
});

// Effective form config: (ticket only) use registrationFormConfig; (table case) merge
const effectiveFormConfig = computed(() => {
  const isTableCase = props.ticketConfig?.table_id != null;
  if (!isTableCase) return registrationFormConfig.value;

  const ticketCfg = registrationFormConfig.value ?? {};
  const tableCfg = registrationFormTableConfig.value ?? {};
  const result = {};

  Object.keys(ticketCfg).forEach((key) => {
    const fieldCfg = ticketCfg[key];
    if (fieldCfg && fieldCfg.visible) result[key] = fieldCfg;
  });

  Object.keys(tableCfg).forEach((key) => {
    const fieldCfg = tableCfg[key];
    if (fieldCfg && fieldCfg.visible && !result[key]) result[key] = fieldCfg;
  });

  return result;
});

// filtered - in this public form we show the merged set directly
const filteredMergedCustomQuestions = computed(() => {
  if (props.regSummary && Array.isArray(props.regSummary.questions)) {
    const answeredIds = new Set(
      props.regSummary.questions
        .filter((q) => {
          if (Array.isArray(q.value)) {
            return q.value.length > 0;
          }
          if (q.value === null || q.value === '') {
            return false;
          }
          if (
            typeof q.value === 'object' &&
            q.value !== null &&
            Object.keys(q.value).length === 0
          ) {
            return false;
          }
          return true;
        })
        .map((q) => q.id),
    );
    return mergedCustomQuestions.value.filter((q) => answeredIds.has(q.id));
  }

  return mergedCustomQuestions.value;
});

// Compute ordered form items respecting field_display_order from the ticket config.
// For TABLE: merges guest ticket order + table-only fields, with the TABLE fix applied.
const orderedFormItems = computed(() => {
  const isTableCase = props.ticketConfig?.table_id != null;
  const ticketOrder = registrationFormConfiguration.value?.field_display_order ?? [];
  let effectiveOrder;

  if (!isTableCase) {
    effectiveOrder = ticketOrder;
  } else {
    const tableOrder = registrationFormTableConfiguration.value?.field_display_order ?? [];

    // When ticketOrder is empty (admin never explicitly saved field_display_order),
    // compute a default from the guest ticket's raw config — mirrors computeDefaultFieldOrder in backend.
    const resolvedTicketOrder = ticketOrder.length
      ? ticketOrder
      : [
          ...Object.entries(registrationFormConfig.value)
            .filter(([, cfg]) => cfg?.visible)
            .map(([key]) => ({ type: 'static', key: String(key) })),
          ...Object.entries(registrationFormConfiguration.value?.meal_choices ?? {})
            .filter(([, cfg]) => cfg?.visible)
            .map(([key]) => ({ type: 'meal', key: String(key) })),
          ...Object.entries(registrationFormConfiguration.value?.custom_questions ?? {})
            .filter(([, cfg]) => cfg?.visible)
            .map(([key]) => ({ type: 'question', key: String(key) })),
        ];

    const ticketKeySet = new Set(resolvedTicketOrder.map((e) => `${e.type}__${e.key}`));
    const tableOnly = tableOrder.filter((e) => !ticketKeySet.has(`${e.type}__${e.key}`));
    effectiveOrder = [...resolvedTicketOrder, ...tableOnly];
  }

  // Fallback: build default order from effective config when no explicit ordering is set
  if (!effectiveOrder.length) {
    effectiveOrder = [
      ...Object.entries(effectiveFormConfig.value)
        .filter(([, cfg]) => cfg?.visible)
        .map(([key]) => ({ type: 'static', key })),
      ...Object.entries(selectedAddonCustomQuestionsAnswer.value)
        .filter(([, addon]) => addon?.addon_type === 'Meal Choice' && addon?.visible)
        .map(([, addon]) => ({ type: 'meal', key: String(addon.addon_id) })),
      ...filteredMergedCustomQuestions.value
        .filter((q) => q?.visible !== false)
        .map((q) => ({ type: 'question', key: String(q.id) })),
    ];
  }

  const items = [];

  // Map meal addon_id -> [tempId, ...] for visible Meal Choice entries
  const mealTempIds = {};
  const seenMealIds = new Set();
  Object.entries(selectedAddonCustomQuestionsAnswer.value).forEach(([tempId, addon]) => {
    if (addon?.addon_type === 'Meal Choice' && addon?.visible) {
      const id = String(addon.addon_id);
      if (!mealTempIds[id]) mealTempIds[id] = [];
      mealTempIds[id].push(tempId);
    }
  });

  // Map question id -> index in filteredMergedCustomQuestions
  const questionIndexMap = {};
  filteredMergedCustomQuestions.value.forEach((q, i) => {
    if (q?.id != null) questionIndexMap[String(q.id)] = i;
  });

  for (const entry of effectiveOrder) {
    if (entry.type === 'static') {
      const config = effectiveFormConfig.value[entry.key];
      if (!config?.visible) continue;
      items.push({ type: 'static', key: String(entry.key), config });
    } else if (entry.type === 'meal') {
      if (seenMealIds.has(entry.key)) continue;
      seenMealIds.add(entry.key);
      const tempIds = mealTempIds[String(entry.key)] ?? [];
      for (const tempId of tempIds) {
        const addon = selectedAddonCustomQuestionsAnswer.value[tempId];
        if (addon?.visible) {
          items.push({ type: 'meal', key: tempId, tempId, addon });
        }
      }
    } else if (entry.type === 'question') {
      const answerIndex = questionIndexMap[String(entry.key)];
      if (answerIndex === undefined) continue;
      const q = filteredMergedCustomQuestions.value[answerIndex];
      if (!q) continue;
      items.push({ type: 'question', key: String(entry.key), question: q, answerIndex });
    }
  }

  return items;
});

// answers for meal choices
const ticketMealChoiceAnswer = ref([]);

// end registration form helpers

onBeforeMount(() => {
  // NOTE: Seed meal-choice visibility from the ticket's own registration form config (meal_choices),
  // which arrives in the same ticket-config payload that mounts this form — NOT from the separately
  // fetched allMealChoices list. Deriving it from allMealChoices meant that if that request lost the
  // race with ticket-config, meals seeded as hidden here and never recovered (FEP-7302).
  const mealChoiceConfigMap = {};
  const mealChoiceConfigSource = registrationFormMealChoices.value ?? {};
  const mealChoiceConfigByAddonId = Array.isArray(mealChoiceConfigSource)
    ? mealChoiceConfigSource.reduce((accum, meal) => {
        if (meal && meal.id != null) accum[String(meal.id)] = meal;
        return accum;
      }, {})
    : mealChoiceConfigSource;
  Object.keys(mealChoiceConfigByAddonId).forEach((addonId) => {
    const mealConfig = mealChoiceConfigByAddonId[addonId] || {};
    mealChoiceConfigMap[addonId] = {
      required: mealConfig.required ?? false,
      visible: mealConfig.visible ?? false,
      toggle_allowed: mealConfig.toggle_allowed ?? true,
    };
  });

  // Loop through all available addons
  props.ticketConfig?.all_available_addons?.forEach((addon) => {
    let mergedAddon = { ...addon };

    if (addon.addon_type === 'Meal Choice') {
      mergedAddon = {
        ...addon,
        ...(mealChoiceConfigMap[addon.id] || {
          required: addon.required ?? false,
          visible: addon.visible ?? false,
          toggle_allowed: addon.toggle_allowed ?? true,
        }),
      };
    } else {
      mergedAddon = {
        ...addon,
        ...{
          required: addon.required ?? false,
          visible: addon.visible ?? false,
          toggle_allowed: addon.toggle_allowed ?? true,
        },
      };
    }

    mergedAddon.variant_answer = '';

    selectedAddOns.value.push(mergedAddon);

    if (!addOnQty.value[addon.id]) {
      addOnQty.value[addon.id] = [uniqueId('_temp')];
      const tempId = addOnQty.value[addon.id][0];
      selectedAddonCustomQuestionsAnswer.value[tempId] = {
        addon_id: mergedAddon.id,
        required: mergedAddon.required,
        toggle_allowed: mergedAddon.toggle_allowed,
        visible: mergedAddon.visible,
        name: mergedAddon.name,
        addon_type: mergedAddon.addon_type,
        customQuestions: mergedAddon.custom_questions,
        hasVariant: mergedAddon.variants?.length,
        variant_question: mergedAddon.variant_question,
        variants: mergedAddon.variants,
        variant_answer: mergedAddon.variant_answer ?? '',
        answers: (mergedAddon.custom_questions || []).map((q) => {
          if (q?.answer_format_config?.type === 'checkbox') return [];
          if (q?.answer_format_config?.type?.includes('text')) return '';
          if (q?.answer_format_config?.type === 'number') return 0;
          if (q?.answer_format_config?.type === 'datetime') return {};
          return '';
        }),
      };
    }
  });

  // initialize based on mergedCustomQuestions (instead of raw ticketConfig.custom_questions)
  mergedCustomQuestions.value?.forEach((q, i) => {
    if (q?.answer_format_config?.type === 'checkbox') {
      return (ticketCustomQuestionsAnswer.value[i] = []);
    }
    if (q?.answer_format_config?.type?.includes('text')) {
      return (ticketCustomQuestionsAnswer.value[i] = '');
    }
    if (q?.answer_format_config?.type === 'number') {
      return (ticketCustomQuestionsAnswer.value[i] = null);
    }
    if (q?.answer_format_config?.type === 'datetime') {
      return (ticketCustomQuestionsAnswer.value[i] = {});
    }
    return (ticketCustomQuestionsAnswer.value[i] = '');
  });

  // add dietary restriction to meal choice addons if missing
  // props.ticketConfig?.all_available_addons?.forEach((a) => {
  //   if (a.addon_type === "Meal Choice") {
  //     if(!a.custom_questions.find(q => q.id === -2)){
  //       a.custom_questions.push({
  //         id: -2,
  //         question: "Do you have any dietary restrictions?",
  //         required: true,
  //         answer_format_config: {
  //           type: "text-short",
  //         },
  //         conditionalConfig: null,
  //         value: null,
  //       });
  //     }
  //   }
  // });

  props.ticketConfig?.included_addons.forEach((addon, i) => {
    const foundMapping = props.ticketConfig?.all_available_addons?.find((a) => a.id === addon.id);
    if (foundMapping) {
      includedAddOns.value.push(foundMapping);
      includedAddOnsCustomQuestionsAnswer.value[i] = [];
      includedAddOnVariant.value[i] = { id: null };
      foundMapping.is_dietary_restriction = foundMapping?.is_dietary_restriction || false;
      foundMapping.custom_questions.forEach((q, index) => {
        if (q?.answer_format_config?.type === 'checkbox') {
          return (includedAddOnsCustomQuestionsAnswer.value[i][index] = []);
        }
        if (q?.answer_format_config?.type?.includes('text')) {
          return (includedAddOnsCustomQuestionsAnswer.value[i][index] = '');
        }
        if (q?.answer_format_config?.type === 'number') {
          return (includedAddOnsCustomQuestionsAnswer.value[i][index] = null);
        }
        if (q?.answer_format_config?.type === 'datetime') {
          return (includedAddOnsCustomQuestionsAnswer.value[i][index] = {});
        }
        return (includedAddOnsCustomQuestionsAnswer.value[i][index] = '');
      });
    }
  });
});

const makeNumberHintMsg = (configOptions) => {
  if (!configOptions) {
    return;
  }

  const min = configOptions.setMin && parseInt(configOptions.min);
  const max = configOptions.setMax && parseInt(configOptions.max);

  switch (true) {
    case typeof min === 'number' && typeof max === 'number':
      return `Enter a number between ${min} and ${max}.`;
    case typeof min === 'number':
      return `Enter a number greater than or equal to ${min}`;
    case typeof max === 'number':
      return `Enter a number less than or equal to ${max}`;
    default:
      return `Enter a number`;
  }
};

const handleNumberUpdateAddon = (number, config, arrToUpdate, index) => {
  if (number === '' || number === null || number === undefined) {
    arrToUpdate[index] = null;
    return;
  }
  const formattedNum = Number(number);

  if (isNaN(formattedNum)) {
    arrToUpdate[index] = null;
    return;
  }
  arrToUpdate[index] = formattedNum;
};

const handleNumberUpdate = (number, config, index) => {
  if (number === '' || number === null || number === undefined) {
    ticketCustomQuestionsAnswer.value[index] = null;
    return;
  }

  const formattedNum = Number(number);

  if (isNaN(formattedNum)) {
    ticketCustomQuestionsAnswer.value[index] = null;
    return;
  }
  ticketCustomQuestionsAnswer.value[index] = formattedNum;
};

const shouldShowQuestion = (question, addonType = false, customQuestions, answer) => {
  // If no conditionalConfig, always show the question
  if (!question.conditionalConfig) {
    return true;
  }

  const config = question.conditionalConfig;
  const controlQuestionId = config.question_id;

  let controlQuestionIndex, controlValue;

  if (!addonType) {
    // Find control question index within the merged custom questions so conditionals align with displayed questions
    controlQuestionIndex = mergedCustomQuestions.value.findIndex((q) => q.id === controlQuestionId);
    if (controlQuestionIndex === -1) {
      return true;
    }
    controlValue = ticketCustomQuestionsAnswer.value[controlQuestionIndex];
  } else {
    if (customQuestions === undefined || customQuestions === null || customQuestions.length === 0) {
      return true;
    }

    let cq = customQuestions ?? [];
    let answers = answer ?? [];

    controlQuestionIndex = cq.findIndex((q) => q.id === controlQuestionId);

    if (controlQuestionIndex === -1) {
      return true;
    }

    controlValue = answers[controlQuestionIndex];
  }

  return handleConditionalQuestionShow(config, controlValue);
};

const handleConditionalQuestionShow = (config, controlValue) => {
  // If the control value is not set yet, hide dependent questions
  if (controlValue === undefined || controlValue === null || controlValue === '') {
    return false;
  }

  // Convert to appropriate types only for numeric comparisons
  const comparisonValue = config.value;
  let controlNumValue, comparisonNumValue;

  if (['is greater', 'is less than'].includes(config.answer)) {
    controlNumValue = Number(controlValue);
    comparisonNumValue = Number(comparisonValue);
    if (isNaN(controlNumValue) || isNaN(comparisonNumValue)) {
      return false;
    }
  }

  // Handle different comparison operators
  let conditionMet = false;

  switch (config.answer) {
    case 'is':
      // For booleans, convert string 'true'/'false' to actual boolean
      if (comparisonValue === 'true' || comparisonValue === 'false') {
        conditionMet = controlValue.toString() === comparisonValue;
      } else {
        conditionMet = controlValue == comparisonValue;
      }
      break;
    case 'is not':
      if (comparisonValue === 'true' || comparisonValue === 'false') {
        conditionMet = controlValue.toString() !== comparisonValue;
      } else {
        conditionMet = controlValue != comparisonValue;
      }
      break;
    case 'is greater':
      conditionMet = controlNumValue > comparisonNumValue;
      break;
    case 'is less than':
      conditionMet = controlNumValue < comparisonNumValue;
      break;
    default:
      conditionMet = true;
  }

  // Apply the action based on whether condition is met
  if (config.action === 'hide') {
    return !conditionMet; // Hide when condition is met, show when it's not
  } else {
    return conditionMet; // Show when condition is met (default behavior)
  }
};

const checkSelectedAddOns = ref(true);

watch(
  selectedAddonCustomQuestionsAnswer,
  () => {
    checkSelectedAddOns.value = checkSelectedAddOnsFunc();
  },
  {
    deep: true,
  },
);

// Add watchers for handling conditional questions
watch(
  ticketCustomQuestionsAnswer,
  () => {
    // This will cause the UI to re-render when answers change
    // which will then reevaluate conditionals
  },
  {
    deep: true,
  },
);

watch(
  includedAddOnsCustomQuestionsAnswer,
  () => {
    // This will cause the UI to re-render when included addon answers change
    // which will then reevaluate conditionals
  },
  {
    deep: true,
  },
);

const checkSelectedAddOnsFunc = () =>
  selectedAddOns.value?.every((addon) => {
    return addon.custom_questions?.every((q, i) => {
      return addOnQty.value[addon.id].every((tempId) => {
        if (addon.hasVariant && !addon.variant_answer) return false;
        if (q?.answer_format_config?.type === 'number') {
          const value = selectedAddonCustomQuestionsAnswer.value[tempId]?.answers[i];
          const config = q?.answer_format_config?.options;

          if (value === null || value === undefined || value === '') {
            return !q.required;
          }

          const numValue = Number(value);

          if (isNaN(numValue)) {
            return false;
          }

          if (config?.setMin && numValue < Number(config?.min)) {
            return false;
          }
          if (config?.setMax && numValue > Number(config?.max)) {
            return false;
          }

          return true;
        }

        if (q.required && q.id !== -2) {
          if (isEmpty(selectedAddonCustomQuestionsAnswer.value[tempId]?.answers[i])) {
            return false;
          }
        }
        return true;
      });
    });
  });

const dateTimeRangeErrors = ref({});
const includedAddonDateTimeRangeErrors = ref({});
const selectedAddonDateTimeRangeErrors = ref({});

const validateDateTimeRange = (index) => {
  const dateData = ticketCustomQuestionsAnswer.value[index];
  dateTimeRangeErrors.value[index] = '';

  // If we have both dates, validate them
  if (dateData.start_date && dateData.end_date) {
    const startDate = new Date(dateData.start_date);
    const endDate = new Date(dateData.end_date);

    // If dates are different, we just need to compare dates
    if (startDate.getTime() !== endDate.getTime()) {
      if (endDate < startDate) {
        dateTimeRangeErrors.value[index] = 'End date cannot be before start date';
        return;
      }
    }
    // If dates are same and we have times, compare times
    else if (dateData.start_time && dateData.end_time) {
      const [startHours, startMinutes] = dateData.start_time.split(':').map(Number);
      const [endHours, endMinutes] = dateData.end_time.split(':').map(Number);

      if (endHours < startHours || (endHours === startHours && endMinutes < startMinutes)) {
        dateTimeRangeErrors.value[index] = 'End time cannot be before start time on the same day';
        return;
      }
    }
  }
};

const validateIncludedAddonDateTimeRange = (addonIndex, questionIndex) => {
  const dateData = includedAddOnsCustomQuestionsAnswer.value[addonIndex][questionIndex];
  const key = `${addonIndex}_${questionIndex}`;
  includedAddonDateTimeRangeErrors.value[key] = '';

  // If we have both dates, validate them
  if (dateData.start_date && dateData.end_date) {
    const startDate = new Date(dateData.start_date);
    const endDate = new Date(dateData.end_date);

    // If dates are different, we just need to compare dates
    if (startDate.getTime() !== endDate.getTime()) {
      if (endDate < startDate) {
        includedAddonDateTimeRangeErrors.value[key] = 'End date cannot be before start date';
        return;
      }
    }
    // If dates are same and we have times, compare times
    else if (dateData.start_time && dateData.end_time) {
      const [startHours, startMinutes] = dateData.start_time.split(':').map(Number);
      const [endHours, endMinutes] = dateData.end_time.split(':').map(Number);

      if (endHours < startHours || (endHours === startHours && endMinutes < startMinutes)) {
        includedAddonDateTimeRangeErrors.value[key] =
          'End time cannot be before start time on the same day';
        return;
      }
    }
  }
};

const validateSelectedAddonDateTimeRange = (tempId, questionIndex) => {
  const dateData = selectedAddonCustomQuestionsAnswer.value[tempId].answers[questionIndex];
  const key = `${tempId}_${questionIndex}`;
  selectedAddonDateTimeRangeErrors.value[key] = '';

  // If we have both dates, validate them
  if (dateData.start_date && dateData.end_date) {
    const startDate = new Date(dateData.start_date);
    const endDate = new Date(dateData.end_date);

    // If dates are different, we just need to compare dates
    if (startDate.getTime() !== endDate.getTime()) {
      if (endDate < startDate) {
        selectedAddonDateTimeRangeErrors.value[key] = 'End date cannot be before start date';
        return;
      }
    }
    // If dates are same and we have times, compare times
    else if (dateData.start_time && dateData.end_time) {
      const [startHours, startMinutes] = dateData.start_time.split(':').map(Number);
      const [endHours, endMinutes] = dateData.end_time.split(':').map(Number);

      if (endHours < startHours || (endHours === startHours && endMinutes < startMinutes)) {
        selectedAddonDateTimeRangeErrors.value[key] =
          'End time cannot be before start time on the same day';
        return;
      }
    }
  }
};

const isFieldValid = (fieldConfig, value) => {
  // Only validate if both visible and required are true
  if (fieldConfig?.visible && fieldConfig?.required) {
    return !!value;
  }
  return true;
};

const allMandatoryDataCollected = computed(() => {
  const customQuestionTicketCheck =
    filteredMergedCustomQuestions.value?.every((question, index) => {
      if (question?.answer_format_config?.type === 'number') {
        const value = ticketCustomQuestionsAnswer.value[index];
        const config = question?.answer_format_config?.options;

        if (value === null || value === undefined || value === '') {
          return !question.required;
        }

        const numValue = Number(value);

        if (isNaN(numValue)) {
          return false;
        }

        if (config?.setMin && numValue < Number(config?.min)) {
          return false;
        }
        if (config?.setMax && numValue > Number(config?.max)) {
          return false;
        }

        return true;
      }

      if (question?.answer_format_config?.type === 'datetime') {
        if (dateTimeRangeErrors.value[index]) {
          return false;
        }
      }

      if (question.visible && question.required) {
        if (isEmpty(ticketCustomQuestionsAnswer.value[index])) {
          return false;
        }
      }
      return true;
    }) ?? true;

  const includedAddonsQuestionsCheck =
    includedAddOns.value?.every((addon, index) => {
      if (addon?.variants?.length) {
        if (!includedAddOnVariant.value[index]?.name) {
          return false;
        }
      }
      return addon.custom_questions.every((q, i) => {
        if (q?.answer_format_config?.type === 'number') {
          const value = includedAddOnsCustomQuestionsAnswer.value[index][i];
          const config = q?.answer_format_config?.options;

          if (value === null || value === undefined || value === '') {
            return !q.required;
          }

          const numValue = Number(value);

          if (isNaN(numValue)) {
            return false;
          }

          if (config?.setMin && numValue < Number(config?.min)) {
            return false;
          }
          if (config?.setMax && numValue > Number(config?.max)) {
            return false;
          }

          return true;
        }
        if (q.required && q.id !== -2) {
          if (isEmpty(includedAddOnsCustomQuestionsAnswer.value[index][i])) {
            return false;
          }
        }
        return true;
      });
    }) ?? true;

  const selectedAddonsVariantCheck =
    selectedAddOns.value?.every((addon) => {
      if (addon?.variants?.length && addon?.visible && addon?.required) {
        return addOnQty.value[addon.id]?.every((tempId) => {
          const answerObj = selectedAddonCustomQuestionsAnswer.value[tempId];
          return answerObj?.variant_answer?.name;
        });
      }
      return true;
    }) ?? true;

  const nonIncludedAddonsQuestionsCheck = checkSelectedAddOns.value ?? true;

  if (!isFieldValid(effectiveFormConfig?.value.name, first_name.value)) {
    return false;
  }
  if (!isFieldValid(effectiveFormConfig?.value.name, last_name.value)) {
    return false;
  }
  // Email must be present if required, and if user has entered something it must be a valid email
  if (!isFieldValid(effectiveFormConfig?.value.email, email.value)) {
    return false;
  }
  if (email.value && !isValidEmail(email.value)) {
    return false;
  }
  if (!isFieldValid(effectiveFormConfig?.value.phone_number, phone_number.value)) {
    return false;
  }
  if (!isFieldValid(effectiveFormConfig?.value.affiliation, affiliationId.value)) {
    return false;
  }
  if (!isFieldValid(effectiveFormConfig?.value.registration_request, registration_request.value)) {
    return false;
  }
  if (!isFieldValid(effectiveFormConfig?.value.dietary_restrictions, dietary_restrictions.value)) {
    return false;
  }

  if (
    !(
      (props.ticketConfig.ticket_type === 'donation' &&
        donation.value >= props.ticketConfig?.ticket_config?.min_amount) ||
      props.ticketConfig.ticket_type !== 'donation'
    )
  ) {
    return false;
  }

  if (!customQuestionTicketCheck) {
    return false;
  }

  if (!includedAddonsQuestionsCheck) {
    return false;
  }

  if (!selectedAddonsVariantCheck) {
    return false;
  }

  if (!nonIncludedAddonsQuestionsCheck) {
    return false;
  }

  return true;
});

const handlePrimaryContactPhone = (value) =>
  (phone_number.value = mask.unmasked(value.replace(/[^0-9]/g, '')));

const addonOutput = () => {
  const results = [];
  selectedAddOns.value.forEach((addon) => {
    addOnQty.value[addon.id].forEach((tempId) => {
      const answerObj = selectedAddonCustomQuestionsAnswer.value[tempId];
      // Only include if any answer or variant_answer is filled
      const hasAnswer = answerObj?.answers?.some((ans) => {
        if (Array.isArray(ans)) return ans.length > 0;
        return ans !== null && ans !== '';
      });
      const hasVariant = answerObj?.variant_answer && answerObj?.variant_answer !== '';
      if (hasAnswer || hasVariant) {
        results.push({
          id: answerObj.addon_id,
          name: answerObj.name,
          is_included: true,
          variant: answerObj.hasVariant
            ? {
                id: answerObj.variant_answer.id ?? addon.id,
                name: answerObj.variant_answer.name,
                price: answerObj.variant_answer.alacart_price,
              }
            : null,
          custom_questions:
            answerObj.customQuestions?.map((q, i) => ({
              id: q.id,
              value: answerObj.answers[i],
            })) || [],
        });
      }
    });
  });
  return results;
};

const handleSave = () => {
  emit('save-registration', {
    ticket_id: props.ticketConfig.ticket_id,
    table_id: props.ticketConfig.table_id,
    first_name: first_name.value,
    last_name: last_name.value,
    email: email.value,
    phone_number: phone_number.value || null,
    address1: address1.value || null,
    address2: address2.value || null,
    city: city.value || null,
    state: state.value || null,
    postal_code: postal_code.value ? Number(postal_code.value) : null,
    country_code:
      (country.value && country.value.acronym) ||
      (typeof country.value === 'string' ? country.value : null),
    affiliationId: affiliationId.value,
    persistedAffiliation: persistedAffiliation.value,
    availableAffiliationData: availableAffiliationData.value,
    groupHostToken: groupHostToken.value,
    registration_request: registration_request.value,
    dietary_restrictions: dietary_restrictions.value || null,
    ticket_price: props.ticketConfig.ticket_type === 'donation' ? donation.value : null,
    custom_questions:
      mergedCustomQuestions.value
        ?.map((q, i) => {
          const val = ticketCustomQuestionsAnswer.value[i];
          if (Array.isArray(val)) {
            if (!val.length) return null;
          } else if (val === null || val === '') {
            return null;
          } else if (typeof val === 'object' && val !== null && Object.keys(val).length === 0) {
            return null;
          }
          return { id: q.id, value: val };
        })
        .filter(Boolean) || [],
    add_ons: [
      ...includedAddOns.value.map((a, i) => ({
        id: a.id,
        name: a.name,
        is_dietary_restriction: a?.is_dietary_restriction,
        variant:
          a.variants && includedAddOnVariant.value[i].id
            ? {
                id: includedAddOnVariant.value[i].id,
                name: includedAddOnVariant.value[i]?.name,
              }
            : null,
        is_included: true,
        custom_questions: a?.custom_questions?.map((q, k) => ({
          id: q.id,
          value: includedAddOnsCustomQuestionsAnswer.value[i][k],
        })),
      })),
      ...addonOutput(),
    ],
    selectedHostInviteCode: selectedHostInviteCode.value,
    invite_others: inviteOthers.value,
  });
  emit('stash-state', {
    first_name: first_name.value,
    last_name: last_name.value,
    email: email.value,
    phone_number: phone_number.value,
    address1: address1.value,
    address2: address2.value,
    city: city.value,
    state: state.value,
    postal_code: postal_code.value ? Number(postal_code.value) : null,
    country_code:
      (country.value && country.value.acronym) ||
      (typeof country.value === 'string' ? country.value : null),
    affiliationId: affiliationId.value,
    persistedAffiliation: persistedAffiliation.value,
    availableAffiliationData: availableAffiliationData.value,
    groupHostToken: groupHostToken.value,
    registration_request: registration_request.value,
    dietary_restrictions: dietary_restrictions.value,
    selectedAddOns: selectedAddOns.value,
    includedAddOns: includedAddOns.value,
    includedAddOnsCustomQuestionsAnswer: includedAddOnsCustomQuestionsAnswer.value,
    addOnQty: addOnQty.value,
    ticketCustomQuestionsAnswer: ticketCustomQuestionsAnswer.value,
    selectedAddonCustomQuestionsAnswer: selectedAddonCustomQuestionsAnswer.value,
    includedAddOnVariant: includedAddOnVariant.value,
    donation: donation.value,
    selectedDonationType: selectedDonationType.value,
    customDonation: customDonation.value,
    selectedHostInviteCode: selectedHostInviteCode.value,
    invite_others: inviteOthers.value,
  });
};

const affilitationUrl = computed(
  () =>
    `${BaseUrl.get(ENDPOINTS.EVENTS.GET_AFFILIATIONS)}${ENDPOINTS.EVENTS.GET_AFFILIATIONS}?customer_id=${
      props.eventData?.customer_id
    }`,
);

const { data: affiliations, error: errorGettingAffiliations } = useFetch(
  affilitationUrl,
  {
    headers: Headers,
    credentials: 'include',
  },
  {
    immediate: true,
  },
);

watch(
  affiliations,
  (newData) => {
    if (newData) {
      availableAffiliationData.value = sortAffiliations(newData.result || []);
    }
  },
  { immediate: true },
);

watch(errorGettingAffiliations, (newError) => {
  if (newError) {
    console.error('Error fetching affiliations:', newError);
  }
});

onMounted(() => {
  if (props.savedData) {
    first_name.value = props.savedData.first_name;
    last_name.value = props.savedData.last_name;
    email.value = props.savedData.email;
    phone_number.value = props.savedData.phone_number;
    affiliationId.value = props.savedData.affiliationId;
    address1.value = props.savedData.address1;
    address2.value = props.savedData.address2;
    city.value = props.savedData.city;
    state.value = props.savedData.state ?? null;
    postal_code.value = props.savedData.postal_code;
    country.value = findCountryByCode(props.savedData.country_code);
    ((persistedAffiliation.value = props.savedData.persistedAffiliation),
      (groupHostToken.value = props.savedData.groupHostToken));
    registration_request.value = props.savedData.registration_request;
    dietary_restrictions.value = props.savedData.dietary_restrictions;
    selectedAddOns.value = props.savedData.selectedAddOns;
    includedAddOns.value = props.savedData.includedAddOns;
    includedAddOnsCustomQuestionsAnswer.value = props.savedData.includedAddOnsCustomQuestionsAnswer;
    addOnQty.value = props.savedData.addOnQty;
    ticketCustomQuestionsAnswer.value = props.savedData.ticketCustomQuestionsAnswer;
    selectedAddonCustomQuestionsAnswer.value = props.savedData.selectedAddonCustomQuestionsAnswer;
    includedAddOnVariant.value = props.savedData.includedAddOnVariant;
    donation.value = props.savedData.donation;
    selectedDonationType.value = props.savedData.selectedDonationType;
    customDonation.value = props.savedData.customDonation;
    selectedHostInviteCode.value = props.savedData.selectedHostInviteCode ?? null;
    inviteOthers.value =
      !props.isPrepaidReg && props.isPrimary && (props.savedData.invite_others || false);
  }
  if (
    !props.isPrepaidReg &&
    props.isPrimary &&
    props.ticketConfig?.guest_invite_mode === 'automatic' &&
    props.ticketConfig?.allow_guest_invites
  ) {
    inviteOthers.value = true;
  }
});

const validateNumberInput = (val, configOptions) => {
  if (!configOptions) return true;

  const numVal = Number(val);

  if (configOptions.setMin && numVal < configOptions.min) {
    return `Value must be at least ${configOptions.min}`;
  }

  if (configOptions.setMax && numVal > configOptions.max) {
    return `Value must not exceed ${configOptions.max}`;
  }

  return true;
};
</script>

<style scoped>
.donationButton {
  width: 21.25rem;
  height: 3.6875rem;
  border-radius: 0.3125rem;
  border-width: 0.0625rem;
  opacity: 1;
  margin-right: 0.75rem;
  color: #525252 !important;
}

.selected-donation {
  border: 2px solid #0e6cb3 !important;
}

.donationButton:not(.selected-donation) {
  border: 1px solid #ccc !important;
  background-color: white !important;
}

.donation-label {
  font-weight: 400 !important;
  font-size: 14px !important;
  color: #525252 !important;
}
</style>
